import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { AuthHero } from '../../components/AuthHero';
import { isNative } from '../../capacitorInit';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password, needsMfa ? totp : undefined);
      if (result.mfaRequired) { setNeedsMfa(true); setLoading(false); return; }
      // A "you've been tagged" email link carries taggedEventId either directly
      // in the URL, or (if the visitor bounced here from AcceptInvite's "Sign in
      // instead" link) stashed in sessionStorage — see AcceptInvite.tsx.
      const stashedTaggedEventId = sessionStorage.getItem('pendingTaggedEventId');
      const taggedEventId = searchParams.get('taggedEventId') || stashedTaggedEventId;
      if (stashedTaggedEventId) sessionStorage.removeItem('pendingTaggedEventId');
      // Otherwise, back to the page PrivateRoute bounced them from, if any.
      const from = (location.state as { from?: string } | null)?.from;
      const returnTo = from?.startsWith('/') && !from.startsWith('//') ? from : '/events';
      navigate(taggedEventId ? `/events/${taggedEventId}` : returnTo);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col desktop:flex-row">
      {/* Native-only tagline, above everything else — web keeps the existing hero/logo as the top element */}
      {isNative && (
        <p className="bg-indigo-800 text-indigo-100 text-center text-xs font-medium px-4 py-2.5">
          Your family's private photo and voice memory archive.
        </p>
      )}

      {/* Left: marketing hero — full on desktop, compact on mobile */}
      <AuthHero />

      {/* Right: sign-in form */}
      <div className={`flex-1 desktop:flex-none desktop:w-[42%] flex ${isNative ? 'items-start overflow-y-auto' : 'items-center'} justify-center p-6 lg:p-10 bg-[#F8F9FF]`}>
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 w-full p-8 lg:p-10">
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-1">Welcome back</h2>
          <p className="text-stone-500 text-sm mb-6">Sign in to your family memories.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!needsMfa ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required autoFocus
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-stone-700">Password</label>
                    <Link to="/forgot-password" className="text-xs text-indigo-600 hover:underline">Forgot password?</Link>
                  </div>
                  <input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Authenticator Code</label>
                <input
                  type="text" value={totp} onChange={e => setTotp(e.target.value)}
                  required autoFocus maxLength={6}
                  placeholder="6-digit code"
                  className="w-full border border-stone-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 tracking-widest text-center text-lg"
                />
              </div>
            )}

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <button
              type="submit" disabled={loading}
              className="w-full bg-indigo-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Signing in…' : needsMfa ? 'Verify Code' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-stone-500">
            New to EventsCarousel?{' '}
            <Link to="/register" className="text-indigo-600 font-medium hover:underline">Create a free account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
